<template>
  <v-card color="darknavyblue" elevation="5" class="pa-4">
    <v-card-title>{{ title }}</v-card-title>
    <v-card-text>
      <v-text-field
        v-if="lockFile"
        :model-value="importFile?.name ?? ''"
        label="Import Tarball"
        append-inner-icon="mdi-lock"
        prepend-icon="mdi-import"
        hint="Selected tarball for import"
        persistent-hint
        disabled
        readonly
      />
      <v-file-input
        v-else
        v-model="importFile"
        :disabled="importLoading"
        label="Import Tarball"
        prepend-icon="mdi-import"
        hint="Select a tarball to import"
        accept="application/gzip"
        persistent-hint
      />
      <v-select
        v-model="importEnvironment"
        class="mt-4"
        :disabled="importLoading"
        :items="importEnvironmentItems"
        label="Target Environment"
        prepend-icon="mdi-earth"
        hint="Select the environment to import the policy into"
        persistent-hint
      />
      <div class="ml-10">
        <v-btn
          color="primary"
          class="mt-4"
          :disabled="importDisabled"
          :loading="importLoading"
          @click="handleImport"
        >
          Import Policy
        </v-btn>
        <v-messages
          v-if="importSuccess"
          key="success"
          class="mt-4"
          color="success"
          v-text="'Policy Imported Successfully'"
        />
        <v-messages
          v-if="importError"
          key="error"
          class="mt-4"
          color="error"
          v-text="importError"
        />
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { Environment } from '@policyfly/protobuf/pfinternal'
import { computed, defineComponent, markRaw, ref, watch } from 'vue'

import { useApiStore } from '@/stores/api'

import type { PropType } from 'vue'

export default defineComponent({
  name: 'PolicyImportCard',

  emits: {
    imported: () => true,
  },

  props: {
    tarballFile: { type: Object as PropType<File | null>, default: null },
    lockFile: { type: Boolean, default: false },
    title: { type: String, default: 'Policy Import' },
  },

  setup (props, { emit }) {
    const apiStore = useApiStore()
    const isLocal = window.location.hostname !== 'internal.policyfly.com'

    const importFile = ref<File | null>()
    const importEnvironment = ref<Environment | null>(isLocal ? Environment.ENVIRONMENT_LOCAL : null)
    const importLoading = ref(false)
    const importDisabled = computed(() => {
      return !importFile.value || !importEnvironment.value || importLoading.value
    })
    const importError = ref<string | null>(null)
    const importSuccess = ref(false)

    watch(() => props.tarballFile, (nextFile) => {
      importFile.value = nextFile
      importSuccess.value = false
      importError.value = null
    }, { immediate: true })

    watch(importEnvironment, () => {
      importSuccess.value = false
      importError.value = null
    })

    const importEnvironmentItems = markRaw([
      {
        title: 'Local',
        value: Environment.ENVIRONMENT_LOCAL,
        props: { disabled: !isLocal },
      },
      {
        title: 'Development',
        value: Environment.ENVIRONMENT_DEV,
        props: { disabled: isLocal, subtitle: 'Canary' },
      },
      {
        title: 'Staging',
        value: Environment.ENVIRONMENT_STAGE,
        props: { disabled: isLocal, subtitle: 'RisingSun' },
      },
      {
        title: 'Bugfix',
        value: Environment.ENVIRONMENT_BUGFIX,
        props: { disabled: isLocal },
      },
      {
        title: 'Production',
        value: Environment.ENVIRONMENT_PROD,
        props: { disabled: true },
      },
    ])

    async function handleImport (): Promise<void> {
      try {
        importSuccess.value = false
        importError.value = null
        importLoading.value = true
        const tarball = new Uint8Array(await importFile.value!.arrayBuffer())
        await apiStore.policyImport({
          tarball,
          targetEnvironment: importEnvironment.value!,
        })
        importSuccess.value = true
        importFile.value = null
        emit('imported')
      } catch (error) {
        importError.value = `Import Failed: ${(error as Error).message}`
        console.error(error)
      } finally {
        importLoading.value = false
      }
    }

    return {
      importFile,
      importEnvironment,
      importEnvironmentItems,
      importLoading,
      importDisabled,
      importError,
      importSuccess,
      handleImport,
    }
  },
})
</script>
